/**
 * How a topic state looks (pedagogy spec §3.3.5 item 3): a fill, a border and a glyph per state,
 * never colour alone. The glyphs hold at least 3:1 against their fill (WCAG 1.4.11) in light and
 * dark; the course map's tiles, its legend and the detail panel all draw from here.
 */
import clsx from "clsx";
import type { TopicState } from "@shared/evidence";

const TILE: Record<TopicState, string> = {
  "not-started": "border-ink-300 bg-ink-100 dark:border-ink-600 dark:bg-ink-800",
  tried: "border-accent-300 bg-accent-100 dark:border-accent-700 dark:bg-accent-900",
  "with-help": "border-amber-600 bg-amber-100 dark:border-amber-500 dark:bg-amber-950",
  "on-your-own": "border-accent-400 bg-accent-300 dark:border-accent-300 dark:bg-accent-500",
  secure: "border-accent-800 bg-accent-700 dark:border-accent-300 dark:bg-accent-600",
};

const GLYPH: Record<TopicState, string> = {
  "not-started": "",
  tried: "text-accent-700 dark:text-accent-200",
  "with-help": "text-amber-700 dark:text-amber-300",
  "on-your-own": "text-accent-900 dark:text-white",
  secure: "text-white",
};

/** The square a state is drawn on: `size` is its edge (the map's tile is 28 px, the legend's 20 px). */
export function stateTileClass(state: TopicState, size: "md" | "sm" = "md"): string {
  return clsx(
    "inline-flex shrink-0 items-center justify-center rounded-md border",
    size === "md" ? "h-7 w-7" : "h-5 w-5",
    TILE[state],
    GLYPH[state],
  );
}

/** The state's mark: empty, a dot, a diagonal half, a filled circle, a check. Decorative. */
export function StateGlyph({ state }: { state: TopicState }) {
  if (state === "not-started") return null;
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true" focusable="false">
      {state === "tried" && <circle cx="8" cy="8" r="2.25" fill="currentColor" />}
      {state === "with-help" && (
        <>
          <rect
            x="2.25"
            y="2.25"
            width="11.5"
            height="11.5"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path d="M13.75 2.25 V13.75 H2.25 Z" fill="currentColor" />
        </>
      )}
      {state === "on-your-own" && <circle cx="8" cy="8" r="5" fill="currentColor" />}
      {state === "secure" && (
        <path
          d="M3.5 8.5 L6.75 11.75 L12.5 4.75"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/** A state swatch without behaviour: the legend and the detail panel. */
export function StateSwatch({ state, size = "sm" }: { state: TopicState; size?: "md" | "sm" }) {
  return (
    <span className={stateTileClass(state, size)} aria-hidden="true">
      <StateGlyph state={state} />
    </span>
  );
}
